import { useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetQuoteHeadingPreferences,
  useSaveQuoteHeadingPreferences,
  getGetQuoteHeadingPreferencesQueryKey,
  usePatchQuoteHeadingName as useGeneratedPatchQuoteHeadingName,
  type NavigationHeadingNameEdit as GeneratedNavigationHeadingNameEdit,
} from "@workspace/api-client-react";

export type NavigationHeadingNameEdit = GeneratedNavigationHeadingNameEdit;
import type { QuoteHeadingNames } from "@workspace/api-zod/quote-headings";
import { useAuth } from "@/hooks/use-auth";

const EMPTY: QuoteHeadingNames = {};

/** Cache key scoped to the signed-in account so one builder never sees another's names. */
export function getAccountQuoteHeadingKey(accountId: string | number | null | undefined) {
  return [...getGetQuoteHeadingPreferencesQueryKey(), { account: accountId ?? null }] as const;
}

export function useQuoteHeadingPreferences(enabled = true) {
  const { user } = useAuth();
  const accountId = (user as { id?: string | number } | null | undefined)?.id ?? null;
  const queryKey = getAccountQuoteHeadingKey(accountId);
  const query = useGetQuoteHeadingPreferences({
    query: { queryKey, enabled: enabled && !!user && !!accountId, staleTime: 30_000 },
  });
  const names = useMemo<QuoteHeadingNames>(() => query.data?.names ?? EMPTY, [query.data]);
  return {
    names,
    queryKey,
    accountId,
    isLoading: enabled && !!accountId && query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Replaces the whole mapping. Pass {} to reset every heading to its default. */
export function useSaveQuoteHeadingNames() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const accountId = (user as { id?: string | number } | null | undefined)?.id ?? null;
  return useSaveQuoteHeadingPreferences({
    mutation: {
      onSuccess: (saved) => {
        const key = getAccountQuoteHeadingKey(accountId);
        queryClient.setQueryData(key, saved);
        queryClient.invalidateQueries({ queryKey: getGetQuoteHeadingPreferencesQueryKey() });
      },
    },
  });
}

/**
 * Saves ONE heading name (empty value resets). The initiating account is
 * captured in onMutate context so a late result only ever patches that
 * account's cache, never whichever account is signed in when it lands.
 */
export function usePatchQuoteHeadingName() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const accountId = (user as { id?: string | number } | null | undefined)?.id ?? null;
  const accountRef = useRef(accountId);
  accountRef.current = accountId;
  const m = useGeneratedPatchQuoteHeadingName<unknown, { accountId: string | number | null }>({
    mutation: {
      onMutate: () => ({ accountId: accountRef.current }),
      onSuccess: (saved, _vars, context) => {
        const initiator = context?.accountId ?? null;
        queryClient.setQueryData(getAccountQuoteHeadingKey(initiator), saved);
        queryClient.invalidateQueries({ queryKey: getAccountQuoteHeadingKey(initiator) });
      },
    },
  });
  const mutateRaw = m.mutate;
  const mutate = useCallback(
    (body: NavigationHeadingNameEdit, callbacks?: Parameters<typeof mutateRaw>[1]) => mutateRaw({ data: body }, callbacks),
    [mutateRaw],
  );
  return { mutate, isPending: m.isPending };
}
